import { useState, forwardRef, useImperativeHandle } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { ImageUpload } from '../components/ui/ImageUpload';
import { formatCurrency } from '../lib/conversions';
import { RichTextEditor } from '../components/ui/RichTextEditor';
import { useTranslations } from '../i18n';
import { FormLabel } from '../components/ui/FormLabel';
import { SparePartSelector } from './SparePartSelector';
import { SparePartItem } from '../components/cars/SparePartItem';
import { SparePartUsageForm } from './SparePartUsageForm';
import type { SparePart, SparePartFormData } from '../types/spareParts';

interface SparePartFormV2Props {
    carId: string;
    spareParts: SparePartFormData[];
    onChange: (spareParts: SparePartFormData[]) => void;
    readOnly?: boolean;
    preferences?: {
        currency?: string;
        currency_symbol?: string;
        file_count_limit?: number;
        plan_type?: string;
    };
    hideAddButton?: boolean;
    slug: string;
    isAdminView?: boolean;
    currentExpenseId?: string;
    currentNoteId?: string;
}

export interface SparePartFormHandle {
    openSelector: () => void;
}

export const SparePartFormV2 = forwardRef<SparePartFormHandle, SparePartFormV2Props>(({
    carId,
    spareParts,
    onChange,
    readOnly = false,
    preferences,
    hideAddButton = false,
    slug,
    isAdminView = false,
    currentExpenseId,
    currentNoteId
}, ref) => {
    const { t } = useTranslations();
    const [showSelector, setShowSelector] = useState(false);
    const [activeExpandedIndex, setActiveExpandedIndex] = useState<number | null>(null);

    useImperativeHandle(ref, () => ({
        openSelector: () => {
            setActiveExpandedIndex(null);
            setShowSelector(true);
        }
    }));

    const currencySymbol = preferences?.currency_symbol || '€';

    // Add existing part from selector
    const handleSelectPart = (part: SparePart) => {
        const newPart: SparePartFormData = {
            id: part.id,
            name: part.name,
            manufacturer: part.manufacturer,
            part_code: part.part_code,
            link: part.link,
            master_comment: part.comment,
            images: part.images?.map(img => ({
                url: img.image_url,
                thumbnail: img.image_url_thumb,
                comment: img.comment || '',
                log_entry_id: img.log_entry_id,
                note_id: img.note_id
            })) || [],
            quantity: 1,
            price_per_unit: undefined,
            comment: undefined,
            // Statistics for display
            total_uses: part.total_uses,
            avg_price: part.avg_price,
            last_used_at: part.last_used_at
        };

        onChange([newPart, ...spareParts]);
        setShowSelector(false);
        // Expand the new part (index 0) and collapse others
        setActiveExpandedIndex(0);
    };

    // Create new part
    const handleCreateNew = () => {
        const newPart: SparePartFormData = {
            id: undefined, // Will be generated on save
            name: '',
            part_code: undefined,
            link: undefined,
            images: [],
            quantity: 1,
            price_per_unit: undefined,
            comment: undefined
        };

        onChange([newPart, ...spareParts]);
        setShowSelector(false);
        // Expand the new part (index 0) and collapse others
        setActiveExpandedIndex(0);
    };

    // Remove part
    const removeSparePart = (index: number) => {
        const updated = spareParts.filter((_, i) => i !== index);
        onChange(updated);
        // Clean up expanded state
        if (activeExpandedIndex === index) {
            setActiveExpandedIndex(null);
        } else if (activeExpandedIndex !== null && activeExpandedIndex > index) {
            setActiveExpandedIndex(activeExpandedIndex - 1);
        }
    };

    // Update usage fields (quantity, price, comment)
    const handleUsageChange = (index: number, usage: Partial<SparePartFormData>) => {
        const updated = [...spareParts];
        updated[index] = {
            ...updated[index],
            ...usage
        };
        onChange(updated);
    };

    // Update master fields (name, code, link, images)
    const handleMasterChange = (index: number, field: keyof SparePartFormData, value: any) => {
        const updated = [...spareParts];
        updated[index] = {
            ...updated[index],
            [field]: value
        };
        onChange(updated);
    };

    // Toggle expand/collapse
    const toggleExpand = (index: number) => {
        setActiveExpandedIndex(activeExpandedIndex === index ? null : index);
    };

    return (
        <div className="space-y-4">
            {/* Spare Parts List */}
            {spareParts.map((part, index) => {
                const isExpanded = activeExpandedIndex === index;
                const isNewPart = !part.id;
                const total = part.price_per_unit ? part.quantity * part.price_per_unit : undefined;

                return (
                    <div
                        key={index}
                        className="bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden"
                    >
                        {/* Header */}
                        {isNewPart ? (
                            <div
                                className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    toggleExpand(index);
                                }}
                            >
                                <div className="flex-1 min-w-0 p-4">
                                    <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                                        {part.name || t.spareParts?.partNamePlaceholder || 'Unnamed Part'}
                                    </h4>
                                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                        {part.part_code && <span>{part.part_code}</span>}
                                        <span>{part.quantity}x</span>
                                        {part.price_per_unit && (
                                            <span className="font-medium">
                                                {formatCurrency(part.price_per_unit, currencySymbol as any)}
                                            </span>
                                        )}
                                        {total && (
                                            <span className="font-semibold text-blue-600 dark:text-blue-400">
                                                = {formatCurrency(total, currencySymbol as any)}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5 px-4 h-full border-l border-gray-100 dark:border-white/5 bg-gray-50/10 dark:bg-white/5 flex-shrink-0">
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeSparePart(index);
                                            }}
                                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                    <div className="p-1">
                                        {isExpanded ? (
                                            <ChevronUp size={20} className="text-gray-400" />
                                        ) : (
                                            <ChevronDown size={20} className="text-gray-400" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-stretch group/part transition-colors">
                                <div className="flex-1 min-w-0">
                                    <SparePartItem
                                        part={{
                                            ...part,
                                            price: part.price_per_unit,
                                            image_count: part.images?.length
                                        } as any}
                                        slug={slug}
                                        isAdminView={isAdminView}
                                        compact={true}
                                        onClick={() => toggleExpand(index)}
                                        className="!bg-transparent !hover:bg-transparent"
                                    />
                                </div>
                                <div className="flex items-center gap-1 pr-3 pl-1 self-stretch shrink-0 border-l border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/5">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const prefix = isAdminView ? '/admin' : '';
                                            window.open(`${prefix}/${slug}/parts/${part.id}`, '_blank');
                                        }}
                                        className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg transition-colors"
                                        title={t.common?.edit || 'Edit'}
                                    >
                                        <ExternalLink size={18} />
                                    </button>
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeSparePart(index);
                                            }}
                                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                    <div className="w-px h-6 bg-gray-200 dark:bg-white/10 mx-1" />
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            toggleExpand(index);
                                        }}
                                        className="p-2 hover:bg-gray-100 dark:hover:bg-white/10 rounded-lg transition-colors"
                                    >
                                        {isExpanded ? (
                                            <ChevronUp size={20} className="text-gray-400" />
                                        ) : (
                                            <ChevronDown size={20} className="text-gray-400" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Expanded Content */}
                        {isExpanded && (
                            <div className="px-4 pb-4 border-t border-gray-200 dark:border-white/10">
                                {isNewPart ? (
                                    // New Part: Full Form
                                    <div className="space-y-4 mt-4">
                                        {/* Name */}
                                        <div>
                                            <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" isFilled={!!part.name}>
                                                {`${t.spareParts?.nameLabel || 'Name'} *`}
                                            </FormLabel>
                                            <input
                                                type="text"
                                                value={part.name}
                                                onChange={(e) => handleMasterChange(index, 'name', e.target.value)}
                                                disabled={readOnly}
                                                placeholder={t.spareParts?.namePlaceholder || 'e.g. Brake Pads'}
                                                className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Manufacturer */}
                                            <div>
                                                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                    {t.spareParts?.manufacturerLabel || 'Manufacturer'}
                                                </FormLabel>
                                                <input
                                                    type="text"
                                                    value={part.manufacturer || ''}
                                                    onChange={(e) => handleMasterChange(index, 'manufacturer', e.target.value || undefined)}
                                                    disabled={readOnly}
                                                    placeholder={t.spareParts?.manufacturerPlaceholder || 'Bosch, Brembo, etc.'}
                                                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20"
                                                />
                                            </div>

                                            {/* Part Code */}
                                            <div>
                                                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                    {t.spareParts?.partCode || 'Part Code'}
                                                </FormLabel>
                                                <input
                                                    type="text"
                                                    value={part.part_code || ''}
                                                    onChange={(e) => handleMasterChange(index, 'part_code', e.target.value?.toUpperCase() || undefined)}
                                                    disabled={readOnly}
                                                    placeholder={t.spareParts?.codePlaceholder || 'e.g. 5Q0 698 151'}
                                                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20"
                                                />
                                            </div>
                                        </div>


                                        <div className="grid grid-cols-2 gap-4">
                                            {/* Quantity */}
                                            <div>
                                                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" isFilled={!!part.quantity}>
                                                    {t.spareParts?.quantity || 'Quantity'}
                                                </FormLabel>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    step="1"
                                                    value={part.quantity || ''}
                                                    onChange={(e) => handleUsageChange(index, { quantity: e.target.value ? parseInt(e.target.value) : 0 })}
                                                    disabled={readOnly}
                                                    placeholder="1"
                                                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20 disabled:text-gray-500"
                                                />
                                            </div>

                                            {/* Price */}
                                            <div>
                                                <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2" isFilled={!!part.price_per_unit}>
                                                    {t.spareParts?.priceLabel?.replace('{currency}', currencySymbol) || `Price (${currencySymbol})`}
                                                </FormLabel>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={part.price_per_unit || ''}
                                                    onChange={(e) => handleUsageChange(index, { price_per_unit: e.target.value ? parseFloat(e.target.value) : undefined })}
                                                    disabled={readOnly}
                                                    placeholder="0.00"
                                                    className="w-full px-4 py-3 bg-white dark:bg-slate-900/40 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 disabled:bg-gray-50 dark:disabled:bg-slate-900/20"
                                                />
                                            </div>
                                        </div>

                                        {/* Comment */}
                                        <div>
                                            <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                {t.spareParts?.comment || 'Comment'}
                                            </FormLabel>
                                            <RichTextEditor
                                                content={part.comment || ''}
                                                onChange={(value) => handleUsageChange(index, { comment: value || undefined })}
                                                readOnly={readOnly}
                                                placeholder={t.spareParts?.additionalInfoPlaceholder || 'Additional information...'}
                                                minHeight="120px"
                                                maxHeight="400px"
                                            />
                                        </div>

                                        {/* Images */}
                                        <div>
                                            <FormLabel className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                                {t.spareParts?.imagesLabel || 'Images & Files'}
                                            </FormLabel>
                                            <ImageUpload
                                                images={part.images}
                                                onChange={(images) => handleMasterChange(index, 'images', images)}
                                                readOnly={readOnly}
                                                maxImages={20}
                                                folder="spare_parts"
                                                showReceiptToggle={true}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    // Existing Part: Usage Form
                                    <div className="mt-2">
                                        <SparePartUsageForm
                                            sparePart={{
                                                id: part.id!,
                                                car_id: carId,
                                                user_id: '', // Will be set on save
                                                name: part.name,
                                                manufacturer: part.manufacturer,
                                                part_code: part.part_code,
                                                link: part.link,
                                                comment: part.master_comment,
                                                created_at: '',
                                                updated_at: '',
                                                total_uses: part.total_uses,
                                                avg_price: part.avg_price,
                                                last_used_at: part.last_used_at,
                                                images: part.images?.map((img, idx) => ({
                                                    spare_part_id: part.id!,
                                                    image_url: img.url,
                                                    image_url_thumb: img.thumbnail,
                                                    comment: img.comment,
                                                    display_order: idx,
                                                    log_entry_id: img.log_entry_id,
                                                    note_id: img.note_id
                                                }))
                                            }}
                                            usage={{
                                                id: part.id!,
                                                quantity: part.quantity,
                                                price_per_unit: part.price_per_unit,
                                                comment: part.comment,
                                                images: part.images
                                            }}
                                            onChange={(usage) => handleUsageChange(index, usage)}
                                            readOnly={readOnly}
                                            preferences={preferences}
                                            slug={slug}
                                            isAdminView={isAdminView}
                                            currentExpenseId={currentExpenseId}
                                            currentNoteId={currentNoteId}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Add Button */}
            {!readOnly && !hideAddButton && (
                <button
                    type="button"
                    onClick={() => {
                        setActiveExpandedIndex(null);
                        setShowSelector(true);
                    }}
                    className="w-full py-3 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 rounded-xl text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 font-semibold flex items-center justify-center gap-2 transition-all"
                >
                    <Plus size={22} strokeWidth={2.5} />
                    {t.spareParts?.addPart || 'Add Spare Part'}
                </button>
            )}

            {/* Spare Part Selector Modal */}
            {showSelector && (
                <SparePartSelector
                    carId={carId}
                    existingPartIds={spareParts.map(p => p.id).filter(Boolean) as string[]}
                    onSelect={handleSelectPart}
                    onCreateNew={handleCreateNew}
                    onClose={() => setShowSelector(false)}
                />
            )}
        </div>
    );
});
